import React, { useEffect, useState, useContext, useMemo } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import useChats from "../../services/chat/useChats";
import useUser from "../../services/Settings/useUser";
import { toast, ToastContainer } from "react-toastify";
import { KeyRound, Pencil, Plus, RotateCcw, Trash } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";
import PosDrawerPinModal from "./PosDrawerPinModal";
import PaginationComponent from "../../utils/TablePagination";

function UserManagement() {
  const { people, setPeople, getAllUserList } = useChats();
  const { userID } = useContext(GlobalContext);
  const {
    roles,
    inviteUser,
    getUserRoles,
    changeUserStatus,
    deleteUser,
    generatePosDrawerPin,
    getPOSAccessPin,
  } = useUser();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [activeStatus, setActiveStatus] = useState(true);
  const [partialAccess, setPartialAccess] = useState(false);
  const [fullAccess, setFullAccess] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const [inviteUserName, setInviteUserName] = useState("");
  const [inviteUserEmail, setInviteUserEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  // POS Drawer PIN states
  const [posPinModalOpen, setPosPinModalOpen] = useState(false);
  const [selectedPosUser, setSelectedPosUser] = useState(null);
  const [posPinSaving, setPosPinSaving] = useState(false);
  const [existingPosPinData, setExistingPosPinData] = useState(null);
  const [posPinFetching, setPosPinFetching] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const sortedPeople = useMemo(() => {
    return [...(people || [])].sort((a, b) => {
      const aRole = String(a?.user_role_name || "").toLowerCase();
      const bRole = String(b?.user_role_name || "").toLowerCase();

      if (aRole === "admin" && bRole !== "admin") return -1;
      if (aRole !== "admin" && bRole === "admin") return 1;

      return String(a?.user_name || "").localeCompare(
        String(b?.user_name || ""),
      );
    });
  }, [people]);

  const getAccessText = (item) => {
    if (item?.control) {
      return item.control === "full" ? "Full Control" : "Partial Control";
    }

    if (String(item?.user_role_name || "").toLowerCase() === "admin") {
      return "Full Control";
    }

    if (item?.user_role_name) {
      return item.user_role_name;
    }

    return "N/A";
  };

  const getLoggedInUser = () => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  };

  const handleOpenPosPinModal = async (item) => {
    const loggedInUser = getLoggedInUser();

    const payload = {
      uscode: loggedInUser?.userid || userID || "",
      fk_userid: String(item?.id || item?.userid || ""),
    };

    if (!payload.uscode) {
      toast.error("Logged in user code missing");
      return;
    }

    if (!payload.fk_userid) {
      toast.error("FK User ID missing");
      return;
    }

    setSelectedPosUser(item);
    setExistingPosPinData(null);
    setPosPinModalOpen(true);

    try {
      setPosPinFetching(true);

      const res = await getPOSAccessPin(payload);

      const pinData = Array.isArray(res?.response) ? res.response[0] : null;

      if (res?.responseCode === "00" && pinData?.pin) {
        setExistingPosPinData(pinData);
      } else {
        setExistingPosPinData(null);
      }
    } catch (error) {
      console.error("handleOpenPosPinModal error:", error);
      setExistingPosPinData(null);
      toast.error("Unable to fetch existing POS PIN");
    } finally {
      setPosPinFetching(false);
    }
  };

  const handleSavePosDrawerPin = async (pin) => {
    if (!selectedPosUser) {
      toast.error("User not selected");
      return;
    }

    const loggedInUser = getLoggedInUser();

    const payload = {
      uscode: loggedInUser?.userid || userID || "",
      fk_userid: String(selectedPosUser?.id || selectedPosUser?.userid || ""),
      pin: String(pin),
    };

    if (!payload.uscode) {
      toast.error("Logged in user code missing");
      return;
    }

    if (!payload.fk_userid) {
      toast.error("FK User ID missing");
      return;
    }

    if (!payload.pin || payload.pin.length !== 6) {
      toast.error("PIN must be exactly 6 digits");
      return;
    }

    try {
      setPosPinSaving(true);

      const res = await generatePosDrawerPin(payload);

      if (res?.responseCode === "00") {
        toast.success(
          existingPosPinData?.pin
            ? "POS drawer PIN updated successfully"
            : res?.responseMessage || "POS drawer PIN saved successfully",
        );

        setPosPinModalOpen(false);
        setSelectedPosUser(null);
        setExistingPosPinData(null);
      } else {
        toast.error(res?.responseMessage || "Failed to save POS drawer PIN");
      }
    } catch (error) {
      console.error("handleSavePosDrawerPin error:", error);
      toast.error("Something went wrong while saving POS drawer PIN");
    } finally {
      setPosPinSaving(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      await getAllUserList();
      await getUserRoles();
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };
  const totalPages = Math.ceil(sortedPeople.length / recordsPerPage);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    return sortedPeople.slice(startIndex, startIndex + recordsPerPage);
  }, [sortedPeople, currentPage]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (roles?.length > 0 && !selectedRole) {
      setSelectedRole(roles[0]?.id || "");
    }
  }, [roles, selectedRole]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <>
    <ToastContainer/>
      <div className="p-6 min-h-0 h-full flex flex-col montserrat-font-1">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 v">
          <h2 className="text-lg font-semibold text-gray-700">Invite People</h2>

          <div className="flex gap-2">
            <button
              onClick={loadUsers}
              className="border px-3 py-2 rounded-lg text-white text-sm bg-blue-500 items-center flex gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Refresh
            </button>

            <button
              onClick={() => setInviteModalOpen(true)}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Plus size={16} />
              Invite
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow border overflow-hidden flex-1">
          <div className="overflow-x-auto h-full">
            <div className="max-h-[650px] overflow-y-auto">
              <table className="w-full text-sm bg-white">
                <thead className="sticky top-0 z-30 bg-green-100 text-gray-700 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-6 py-4 text-left">Person</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Mobile</th>
                    <th className="px-6 py-4 text-left">Role</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Access</th>
                    <th className="px-6 py-4 text-center">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="py-10">
                        <div className="flex justify-center">
                          <LoadingScreen text="Loading..." />
                        </div>
                      </td>
                    </tr>
                  ) : sortedPeople?.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-8 text-gray-400"
                      >
                        No Users Found
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map((item, index) => {
                      const roleName = String(item?.user_role_name || "")
                        .trim()
                        .toLowerCase();

                      return (
                        <tr
                          key={item?.pkid || item?.userid || index}
                          className={`group transition-colors ${
                            roleName === "admin"
                              ? "bg-red-100 hover:bg-red-200"
                              : roleName === "cash manager"
                                ? "bg-blue-100 hover:bg-blue-200"
                                : "bg-white hover:bg-slate-50"
                          }`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div
                                className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${
                                  roleName === "admin"
                                    ? "bg-red-50 text-red-600"
                                    : roleName === "cash manager"
                                      ? "bg-blue-50 text-blue-600"
                                      : "bg-green-50 text-green-600"
                                }`}
                              >
                                {(item.user_name || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div>
                                <p className="font-semibold text-slate-800">
                                  {item.user_name || "N/A"}
                                </p>
                                <p className="text-xs text-slate-400">
                                  ID: {item.userid || item.id || "N/A"}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                            {item.user_email || "N/A"}
                          </td>

                          <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                            {item.user_mobile || "N/A"}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                roleName === "admin"
                                  ? "bg-red-50 text-red-600 ring-1 ring-red-100"
                                  : roleName === "cash manager"
                                    ? "bg-blue-50 text-blue-600 ring-1 ring-blue-100"
                                    : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
                              }`}
                            >
                              {item.user_role_name || "N/A"}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                item.user_status === "ACTIVE"
                                  ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                                  : "bg-red-50 text-red-600 ring-1 ring-red-100"
                              }`}
                            >
                              {item.user_status === "ACTIVE"
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                            {getAccessText(item)?.trim() || "N/A"}
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex justify-center items-center gap-2">
                              {roleName !== "admin" && (
                                <>
                                  <button
                                    onClick={() => {
                                      setSelectedUser(item);
                                      setActiveStatus(
                                        item.user_status === "ACTIVE",
                                      );
                                      setSelectedRole(
                                        item.user_role || roles?.[0]?.id || "",
                                      );
                                      setEditModalOpen(true);
                                    }}
                                    className="p-2 rounded-lg text-green-600 bg-green-200 hover:text-green-600 transition"
                                  >
                                    <Pencil size={16} />
                                  </button>

                                  <button
                                    onClick={() => {
                                      setSelectedUser(item);
                                      setSelectedUserId(item.userid);
                                      setDeleteModalOpen(true);
                                    }}
                                    className="p-2 rounded-lg text-red-500 bg-red-200 hover:text-red-600 transition"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </>
                              )}

                              <button
                                onClick={() => handleOpenPosPinModal(item)}
                                className="p-2 rounded-lg text-blue-500 bg-blue-200 hover:text-blue-600 transition"
                                title="POS Drawer PIN"
                              >
                                <KeyRound size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* edit user Access */}

          {editModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
              <div className="bg-white w-[480px] rounded-xl shadow-lg montserrat-font-1">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b montserrat-font-1">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Edit user Access
                  </h2>

                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5 montserrat-font-1">
                  <p className="text-gray-600">
                    Manage Access for{" "}
                    <span className="font-semibold">
                      {selectedUser?.user_name}
                    </span>
                  </p>

                  {/* Active Status */}
                  <div className="flex justify-between items-center montserrat-font-1">
                    <span className="text-gray-700">Active Status</span>

                    <button
                      onClick={() => setActiveStatus(!activeStatus)}
                      className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                        activeStatus ? "bg-green-600" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`bg-white w-4 h-4 rounded-full shadow transform transition ${
                          activeStatus ? "translate-x-6" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Role */}
                  <div className="montserrat-font-1">
                    <label className="text-gray-700 text-sm">Change Role</label>

                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    >
                      {roles?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Partial Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Partial Access</span>

                      <button
                        onClick={() => {
                          setPartialAccess(!partialAccess);
                          setFullAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          partialAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            partialAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      Partial control access typically grants limited
                      permissions, allowing users to perform specific tasks
                      within defined parameters.
                    </p>
                  </div>

                  {/* Full Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Full Access</span>

                      <button
                        onClick={() => {
                          setFullAccess(!fullAccess);
                          setPartialAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          fullAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            fullAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 mt-1">
                      Full control access provides unrestricted authority,
                      enabling users to modify, create, and delete without
                      limitations.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="px-6 py-2 border border-green-600 text-green-600 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      setEditModalOpen(false);
                      const payload = {
                        userid: selectedUser.userid,
                        status: activeStatus ? "Y" : "N",
                      };

                      const res = await changeUserStatus(payload);

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        setPeople((prev) =>
                          prev.map((user) =>
                            user.userid === selectedUser.userid
                              ? {
                                  ...user,
                                  user_status: activeStatus
                                    ? "ACTIVE"
                                    : "INACTIVE",
                                }
                              : user,
                          ),
                        );

                        setEditModalOpen(false);
                      }
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* invite user */}

          {inviteModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4 montserrat-font-1">
              <div className="bg-white w-full max-w-[520px] rounded-xl shadow-lg flex flex-col max-h-[90vh] montserrat-font-1">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0 montserrat-font-1">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Invite People
                  </h2>
                  <button
                    onClick={() => setInviteModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {/* Body — scrollable */}
                <div className="p-6 space-y-5 overflow-y-auto flex-1 montserrat-font-1">
                  <p className="text-gray-600 text-sm font-semibold montserrat-font-2">
                    Invite new members to your business by entering their email
                    or phone number. Customize their access permissions and
                    select the Accounts and tools they can use.
                  </p>

                  {/* Username */}
                  <div>
                    <label className="text-sm text-green-700">UserName</label>
                    <input
                      type="text"
                      value={inviteUserName}
                      onChange={(e) => setInviteUserName(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm text-green-700">Email</label>
                    <input
                      type="email"
                      value={inviteUserEmail}
                      onChange={(e) => setInviteUserEmail(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="text-sm text-green-700">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <p className="text-gray-800 text-sm font-semibold mb-2 montserrat-font-2">
                      Choose the tasks that needs to perform within the Chaafo
                      business portfolio.
                    </p>
                    <label className="text-sm text-green-700">
                      Select Role
                    </label>

                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                    >
                      {roles?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.role_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Partial Access */}
                  <div>
                    <div className="flex justify-between items-center montserrat-font-1">
                      <span className="text-green-700">Partial Access</span>

                      <button
                        onClick={() => {
                          setPartialAccess(!partialAccess);
                          setFullAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          partialAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            partialAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                      Partial control access typically grants limited
                      permissions, allowing users to perform specific tasks
                      within defined parameters.
                    </p>
                  </div>

                  {/* Full Access */}
                  <div className="montserrat-font-1">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">Full Access</span>

                      <button
                        onClick={() => {
                          setFullAccess(!fullAccess);
                          setPartialAccess(false);
                        }}
                        className={`w-12 h-6 flex items-center rounded-full p-1 ${
                          fullAccess ? "bg-green-600" : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow transform ${
                            fullAccess ? "translate-x-6" : ""
                          }`}
                        />
                      </button>
                    </div>

                    <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                      Full control access provides unrestricted authority,
                      enabling users to modify, create, and delete without
                      limitations.
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0 montserrat-font-1">
                  <button
                    onClick={() => setInviteModalOpen(false)}
                    className="px-6 py-2 border border-green-600 text-green-600 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const payload = {
                        userid: userID,
                        username: inviteUserName,
                        email: inviteUserEmail,
                        mobile: phoneNumber,
                        role: selectedRole || roles?.[0]?.id || "",
                      };

                      console.log("INVITE USER PAYLOAD:", payload);

                      if (!payload.userid) {
                        toast.error("User ID missing");
                        return;
                      }

                      if (!payload.role) {
                        toast.error("Please select role");
                        return;
                      }

                      const res = await inviteUser(payload);

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        setInviteModalOpen(false);

                        setInviteUserName("");
                        setInviteUserEmail("");
                        setPhoneNumber("");
                        setSelectedRole("");
                        setPartialAccess(false);
                        setFullAccess(false);

                        await loadUsers();
                      } else {
                        toast.error(
                          res?.responseMessage || "Failed to invite user",
                        );
                      }
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* delete model */}

          {deleteModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1">
              <div className="bg-white w-[400px] rounded-xl shadow-lg montserrat-font-1">
                {/* Header */}
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold text-gray-700">
                    Delete User
                  </h2>
                </div>

                {/* Body */}
                <div className="p-6 space-y-3 text-gray-600 montserrat-font-1">
                  <p>Are you sure you want to delete this user?</p>

                  <div className="bg-gray-50 rounded-lg p-3 border text-sm space-y-1 montserrat-font-2">
                    <p>
                      <span className="font-semibold">Name:</span>{" "}
                      {selectedUser?.user_name}
                    </p>
                    <p>
                      <span className="font-semibold">Email:</span>{" "}
                      {selectedUser?.user_email}
                    </p>
                    <p>
                      <span className="font-semibold">Mobile:</span>{" "}
                      {selectedUser?.user_mobile}
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const res = await deleteUser({
                        userid: selectedUserId,
                      });

                      if (res?.responseCode === "00") {
                        toast.success(res.responseMessage);

                        setDeleteModalOpen(false);

                        // Delete success ke baad fresh list API se reload
                        await loadUsers();
                      } else {
                        toast.error(
                          res?.responseMessage || "Failed to delete user",
                        );
                      }
                    }}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* POS Drawer PIN Modal */}
          <PosDrawerPinModal
            show={posPinModalOpen}
            user={selectedPosUser}
            existingPinData={existingPosPinData}
            isFetchingPin={posPinFetching}
            isSaving={posPinSaving}
            onClose={() => {
              if (posPinSaving || posPinFetching) return;

              setPosPinModalOpen(false);
              setSelectedPosUser(null);
              setExistingPosPinData(null);
            }}
            onSave={handleSavePosDrawerPin}
          />
        </div>
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onNextPage={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          onPrevPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onPageClick={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
}

export default UserManagement;
